import { useCallback } from 'react';

import { formatErrorForLogging } from '@eluve/eluve-errors';
import { useNamedLogger } from '@eluve/logger';

import { useDb } from '../FilesDbProvider';

import { usePeriodicRemoteFileSyncActor } from './usePeriodicRemoteFileSyncMachine';

/**
 * Returns a callback that finds any locally-tracked files for the given
 * appointment in IndexedDB and asks the periodic sync machine to upload them
 * immediately. Files that have already uploaded successfully are removed from
 * IDB, so anything still here is either failed or never-attempted.
 *
 * Use from the web app's retry-processing flow to push pending uploads on the
 * current device alongside the server-side transcript retry. The extension's
 * file sync runs in an offscreen document, so it doesn't use this hook.
 */
export const useTriggerLocalFileReupload = (appointmentId: string) => {
  const logger = useNamedLogger('useTriggerLocalFileReupload');
  const db = useDb();
  const { send } = usePeriodicRemoteFileSyncActor();

  return useCallback(async (): Promise<number> => {
    try {
      const files = await db.appointmentFiles
        .where('appointmentId')
        .equals(appointmentId)
        .toArray();

      if (files.length === 0) {
        return 0;
      }

      logger.info('Triggering local file reupload', {
        appointmentId,
        count: files.length,
      });

      for (const file of files) {
        send({
          type: 'IMMEDIATE_SYNC_AVAILABLE',
          file: {
            ...file,
            attemptCount: 0,
            lastUploadAttemptedAt: new Date().toISOString(),
          },
        });
      }

      return files.length;
    } catch (error) {
      logger.warn('Failed to trigger local file reupload', {
        appointmentId,
        error: formatErrorForLogging(error),
      });
      return 0;
    }
  }, [appointmentId, db, send, logger]);
};
